<app-order-entity-filters [filters]="filters" dateLabel="Dispatch date" (filtersChange)="onFiltersChange($event)">
  <ng-select
    placeholder="Filter by status"
    [items]="statusOptions"
    [clearable]="false"
    [searchable]="false"
    [(ngModel)]="selectedStatus"
    (change)="onStatusChange()"
    bindLabel="label"
    bindValue="value"
    class="flex-grow-1"
    style="min-width: 180px" />

  <button
    class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2"
    (click)="exportDispatched()"
    [disabled]="isExporting">
    @if (isExporting) {
      <span class="spinner-border spinner-border-sm"></span>
    } @else {
      <i class="bi bi-download"></i>
    }
    Export
  </button>
</app-order-entity-filters>

@if (isDataLoading) {
  <app-loading-spinner [message]="'Loading dispatched orders...'" />
} @else {
  @if (shipments.length === 0) {
    <div class="text-center py-5">
      <div class="bg-light d-inline-block p-4 rounded-circle mb-3">
        <i class="bi bi-truck text-muted fs-1"></i>
      </div>
      <h5 class="text-dark fw-bold">No Dispatched Orders Found</h5>
      @if (hasActiveFilters) {
        <p class="text-muted">
          No shipments match the selected status and all of the active filters. Try removing one of the filters above.
        </p>
      } @else {
        <p class="text-muted">No orders have been dispatched yet for the selected status.</p>
      }
    </div>
  } @else {
    <div class="table-responsive">
      <table class="table table-hover align-middle mb-0">
        <thead class="bg-light">
          <tr>
            <th class="ps-4 border-0 text-uppercase small fw-bold text-muted py-3">Shipment No.</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Order Number</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Dealer</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Products</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Transporter</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Dispatch Date</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Expected Delivery</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Delivery Status</th>
          </tr>
        </thead>
        <tbody>
          @for (shipment of shipments; track shipment.id) {
            <tr class="border-bottom" style="cursor: pointer" (click)="viewShipment(shipment.id)">
              <td class="ps-4 py-3 fw-bold text-dark">#{{ shipment.shipment_number }}</td>
              <td class="py-3 text-muted">#{{ shipment.order.order_number }}</td>
              <td class="py-3">
                @if (shipment.order.dealer) {
                  <div class="fw-semibold small">{{ shipment.order.dealer.shop_name }}</div>
                  <small class="text-muted">
                    {{ shipment.order.dealer.first_name }} {{ shipment.order.dealer.last_name }}
                  </small>
                } @else {
                  <span class="text-muted">—</span>
                }
              </td>
              <td class="py-3">
                @if (shipment.items?.length) {
                  <div class="d-flex flex-column gap-2">
                    @for (item of shipment.items; track item.id) {
                      <div class="d-flex justify-content-between align-items-start gap-3">
                        <div>
                          <div class="small fw-semibold">{{ getProductName(item) }}</div>
                          <small class="text-muted">
                            {{ item.order_item?.product_packaging?.packing_size }}
                            {{ item.order_item?.product_packaging?.unit?.short_name }}
                            @if (item.order_item?.product_packaging?.nos) {
                              &times; {{ item.order_item.product_packaging.nos }}
                            }
                            @if (item.order_item?.product_packaging?.packaging_type) {
                              &middot; {{ item.order_item.product_packaging.packaging_type!.name }}
                            }
                          </small>
                        </div>
                        <span class="small fw-semibold text-nowrap">
                          {{ item.box_quantity }} {{ item.box_quantity === 1 ? 'box' : 'boxes' }}
                        </span>
                      </div>
                    }

                    <div class="d-flex justify-content-between align-items-center gap-3 border-top pt-2">
                      <span class="small fw-semibold text-muted">Total</span>
                      <span class="small fw-bold text-nowrap">
                        {{ getTotalBoxes(shipment) }} {{ getTotalBoxes(shipment) === 1 ? 'box' : 'boxes' }}
                      </span>
                    </div>
                  </div>
                } @else {
                  <span class="text-muted">—</span>
                }
              </td>

              <td class="py-3">
                @if (shipment.transporter) {
                  <div class="d-flex align-items-center gap-2">
                    <i class="bi bi-truck text-muted"></i>
                    <div>
                      <div class="small fw-semibold">
                        {{ shipment.transporter.first_name }} {{ shipment.transporter.last_name }}
                      </div>
                      <small class="text-muted">{{ shipment.transporter.vehicle_number }}</small>
                    </div>
                  </div>
                } @else {
                  <span class="text-muted small">—</span>
                }
              </td>
              <td class="py-3">
                @if (shipment.dispatch_date) {
                  <span class="small">
                    <i class="bi bi-calendar-event me-1"></i>
                    {{ shipment.dispatch_date | date: 'dd MMM yyyy' }}
                  </span>
                } @else {
                  <span class="text-muted">—</span>
                }
              </td>
              <td class="py-3">
                @if (shipment.delivery_date) {
                  <span class="small">
                    <i class="bi bi-calendar-check me-1"></i>
                    {{ shipment.delivery_date | date: 'dd MMM yyyy' }}
                  </span>
                } @else {
                  <span class="text-muted">—</span>
                }
              </td>
              <td class="py-3">
                <span
                  class="app-badge"
                  [ngClass]="{
                    'badge-primary': shipment.status === 'dispatched',
                    'badge-warning': shipment.status === 'in_transit',
                    'badge-success': shipment.status === 'delivered',
                  }">
                  {{ shipment.status | titlecase }}
                </span>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <div class="mt-4">
      <app-pagination
        [totalCount]="totalCount"
        [hasNextPage]="hasNextPage"
        [hasPreviousPage]="hasPreviousPage"
        [currentPage]="currentPage"
        [pageSize]="pageSize"
        (pageChange)="onPageChange($event)" />
    </div>
  }
}
